import { ErrorWrapper } from "@origin-digital/error-wrapper";
import React from "react";
import { Text } from "@origin-digital/ods-core";
import { logger } from "@origin-digital/reporting-client";
import { content } from "../../content";
import { BASE_PATH } from "../../routes.constants";
import styled from "styled-components";

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  isNotFound: boolean;
}

const NOT_FOUND_PATH = "/error-page/404";

const ErrorWrapperContainer = styled.div`
  background-color: ${({ theme }) => theme.palette.background.paper} !important;
  padding: 48px 0;
`;

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, isNotFound: false };
  }

  private static getErrorStatus(error: unknown): number | undefined {
    const candidate = error as {
      status?: number;
      response?: { status?: number };
      fetchResponse?: { status?: number };
    };

    return (
      candidate?.status ??
      candidate?.response?.status ??
      candidate?.fetchResponse?.status
    );
  }

  private static getNotFoundRedirectPath(): string {
    const basePath = (window.__BASEPATH__ || "").replace(/\/+$/, "");

    if (!basePath) {
      return NOT_FOUND_PATH;
    }

    if (!basePath.endsWith(BASE_PATH)) {
      return `${basePath}${NOT_FOUND_PATH}`;
    }

    const siteRoot = basePath.slice(0, -BASE_PATH.length);
    return `${siteRoot}${NOT_FOUND_PATH}`;
  }

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    // Catch all errors and decide specific handling in componentDidCatch.
    return {
      hasError: true,
      isNotFound: ErrorBoundary.getErrorStatus(error) === 404,
    };
  }

  componentDidCatch(error: Error, _errorInfo: React.ErrorInfo): void {
    if (ErrorBoundary.getErrorStatus(error) === 404) {
      window.location.replace(ErrorBoundary.getNotFoundRedirectPath());
      return;
    }
    logger.error("ErrorBoundary caught an error:", error);
  }

  render() {
    if (this.state.hasError) {
      if (this.state.isNotFound) {
        return null;
      }

      if (this.props.fallback) {
        return this.props.fallback;
      }
      return (
        <ErrorWrapperContainer>
          <ErrorWrapper
            header={content.errorBoundary.header}
            body={<Text align="center">{content.errorBoundary.body}</Text>}
            buttonLabel={content.errorBoundary.buttonLabel}
            onClick={() => window.location.reload()}
          />
        </ErrorWrapperContainer>
      );
    }
    return this.props.children;
  }
}
